import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import ServicesContext from '../../contexts/ServicesContext';
import { ProductGrouping } from '../../models/ProductGrouping';
import { OrderItem } from '../../models/OrderItem';
import { OrderSummaryForProductGrouping } from '../../models/OrderSummaryForProductGrouping';
import { AddProductGroupingInput } from '../../services/ProductGroups/AddProductGrouping/AddProductGroupingInput';
import { Button } from '../../common-components/Button/Button';
import { CardProductGroup } from './CardProductGroup/CardProductGroup';
import { FormProductGroupValues, FormProductGroup } from './FormProductGroup/FormProductGroup';
import { FooterContent } from './styles';

interface IModalProductGroupsProps {
  visible: boolean;
  close: (changed?: boolean) => void;
  title: string;
  orderId?: number;
  notList?: boolean;
}

type TypeSummary = {
  type: string;
  items: OrderItem[];
  count: number;
};

const getInitialFormValues = () => new FormProductGroupValues();

const ModalProductGroups: React.FC<IModalProductGroupsProps> = ({
  visible,
  close,
  title,
  orderId,
  notList
}) => {

  const [productGroups, setProductGroups] = React.useState<ProductGrouping[]>([]);
  const [orderItems, setOrderItems] = React.useState<OrderItem[]>([]);
  const [formVisible, setFormVisible] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<FormProductGroupValues>(getInitialFormValues());

  const {
    getProductGroupingsByOrderService,
    getOrderItemsByOrderService,
    addProductGroupingService,
    removeProductGroupingService,
    getOrderSummaryForProductGroupingService,
    toastService,
    alertService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    if (!visible) return;
    getData();
  }, [visible, notList]);

  const body = React.useMemo(() => {
    if (!visible) return null;

    if (formVisible || notList) {
      return (
        <FormProductGroup
          values={formValues}
          orderItems={orderItems}
          productGroups={productGroups}
        />
      );
    }

    const orderHasAnyGroup = productGroups?.length > 0;

    if (!orderHasAnyGroup) {
      return (
        <Alert variant='warning'>
          {strings.NoneProductGroupingFound}
        </Alert>
      );
    }

    return productGroups.map(productGrouping => {
      return (
        <CardProductGroup key={`product-grouping-${productGrouping.id}`} productGrouping={productGrouping} remove={(id) => removeGroup(id)} />
      );
    });
  }, [visible, formVisible, productGroups, formValues, notList]);

  const footer = React.useMemo(() => {
    if (!visible) return null;

    if (formVisible || notList) {
      return (
        <>
          <Button text={strings.Cancel} width='230px' marginRight='20px' click={() => cancelForm()} />
          <Button text={strings.ConfirmProductGroup} width='230px' click={() => confirmGroup()} />
        </>
      );
    }

    return (
      <>
        <Button text={strings.NewProductGroup} width='210px' marginRight='20px' click={() => insertProductGrouping()} />
        <Button text={strings.Update} width='210px' click={() => getData()} />
      </>
    );
  }, [visible, formVisible, productGroups, formValues, notList]);

  if (!visible) return null;

  return (
    <Modal
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton style={{ padding: '10px 1rem', height: '50px' }}>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='modalProductGroupsBody' style={{ maxHeight: '78vh', overflowY: 'auto' }}>
        {body}
      </Modal.Body>

      <Modal.Footer>
        <FooterContent>
          {footer}
        </FooterContent>
      </Modal.Footer>
    </Modal>
  );

  function getData() {
    if (!orderId) return;

    getProductGroupingsByOrderService
      .handle({ orderId: orderId }).then(result => {
        setProductGroups(result?.value?.productGroupings || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });

    getOrderItemsByOrderService
      .handle({ orderId: orderId }).then(result => {
        setOrderItems(result?.value?.items || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });
  }

  function clickCloseModal(changed?: boolean) {
    setFormVisible(false);
    setFormValues(getInitialFormValues());
    close(changed);
  }

  async function insertProductGrouping() {
    if (!await canGroup()) return;
    setFormValues(getInitialFormValues());
    setFormVisible(true);
  }

  function cancelForm() {
    setFormVisible(false);
    setFormValues(getInitialFormValues());
    if (notList) {
      clickCloseModal();
    }
  }

  async function confirmGroup() {
    const hasSelectedItems = formValues?.selectedItems?.length > 0;
    if (!hasSelectedItems) return;

    const typeSummary = getTypeSummary();
    const repeatedTypeList = typeSummary.filter(s => s.count > 1);
    if (repeatedTypeList.length > 0) {
      const firstRepeatedType = repeatedTypeList[0];
      const confirmation = await alertService.confirmHtml(
        `Você seleciou ${firstRepeatedType.count} itens do tipo <strong>${firstRepeatedType.type}</strong>.`+
        `<br>É isso mesmo?`);
      if (!confirmation.isConfirmed) return;
    }

    const input = new AddProductGroupingInput();
    input.orderId = orderId!;
    input.orderItemIdList = [...formValues.selectedItems];
    addProductGroupingService.handle(input)
      .then(result => {
        setFormVisible(false);
        setFormValues(getInitialFormValues());
        getData();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSaving);
      });
  }

  function getTypeSummary() {
    const typeSummary: TypeSummary[] = [];
    formValues.selectedItems.forEach(itemId => {
      const orderItem = orderItems.find(oi => oi.id === itemId);
      let itemType = 'item';
      if (orderItem) {
        const name = orderItem.name.toLowerCase();
        if (name.includes('lente')) {
          itemType = 'lente';
        } else if (name.includes('óculos')) {
          itemType = 'armação';
        }
      }
      const summary = typeSummary.find(s => s.type === itemType);
      if (summary) {
        summary.count += 1;
        if (orderItem) summary.items.push(orderItem);
      } else {
        typeSummary.push({
          count: 1,
          items: orderItem ? [ orderItem ] : [],
          type: itemType
        });
      }
    });
    return typeSummary;
  }

  async function removeGroup(id: number) {
    if (!await canGroup()) return;
    removeProductGroupingService.handle({ id: id })
      .then(result => {
        setFormVisible(false);
        setFormValues(getInitialFormValues());
        getData();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  async function canGroup() {
    const productRecipeLinkingRequestCount = (await getSummary())?.productRecipeLinkingRequestCount || 0;
    if (productRecipeLinkingRequestCount === 0) return true;
    alertService.info('Este pedido já possui receitas vinculadas.');
    return false;
  }

  async function getSummary(): Promise<OrderSummaryForProductGrouping|null|undefined> {
    if (!orderId) return null;
    const result = await getOrderSummaryForProductGroupingService.handle({ orderId: orderId });
    return result?.value;
  }

}

export { ModalProductGroups };